import { useState, useEffect, cloneElement, useRef, RefObject, useCallback, ReactElement } from 'react';
import { ScrollDirectionType, SliderNavButton } from './SliderNavButton';
import NoElementsFound, { NO_ELEMENTS_LC_COLLECTION_TITLES } from '../../components/NoElementsFound';
import { ProductOut } from '../../../store/api';
import {
    StContainerShadowLeft,
    StContainerShadowRight,
    StSliderContainer,
    StSliderOuterWrapper,
    StSliderTileComponentWrapper,
    StSliderWrapper
} from './styledComponents';

export const SCROLL_DIRECTION_LEFT = 'LEFT';
export const SCROLL_DIRECTION_RIGHT = 'RIGHT';

interface Props {
    productGroupProducts: ProductOut[];
    SliderTileComponent: ReactElement;
    shadowOffsetX?: number;
    sliderHeight?: number;
    sliderHeightHover?: number;
}

export function ProductGroupSlider({
    productGroupProducts,
    SliderTileComponent,
    shadowOffsetX = -30,
    sliderHeight = 400,
    sliderHeightHover = 800
}: Props) {
    const [isAnyTileHovered, setIsAnyTileHovered] = useState(false);
    const [isLeftNavVisible, setIsLeftNavVisible] = useState(false);
    const [isRightNavVisible, setIsRightNavVisible] = useState(false);
    const scrollbarRef = useRef<HTMLDivElement>(null);

    const handleSetNavButtonVisibility = useCallback(
        (scrollbarRef: RefObject<HTMLDivElement>) => {
            setIsLeftNavVisible(isNavButtonVisible(scrollbarRef, SCROLL_DIRECTION_LEFT));
            setIsRightNavVisible(isNavButtonVisible(scrollbarRef, SCROLL_DIRECTION_RIGHT));
        },
        [setIsLeftNavVisible, setIsRightNavVisible]
    );

    useEffect(() => {
        handleSetNavButtonVisibility(scrollbarRef);
    }, [handleSetNavButtonVisibility, productGroupProducts]);

    function scroll(scrollDirection: ScrollDirectionType) {
        const TILE_WIDTH = 170;
        const AMOUNT_OF_TILES_TO_SCROLL = 4;

        if (scrollDirection === SCROLL_DIRECTION_LEFT && scrollbarRef.current) {
            scrollbarRef.current.scrollLeft = scrollbarRef.current.scrollLeft + TILE_WIDTH * AMOUNT_OF_TILES_TO_SCROLL;
        } else if (scrollDirection === SCROLL_DIRECTION_RIGHT && scrollbarRef.current) {
            scrollbarRef.current.scrollLeft = scrollbarRef.current.scrollLeft - TILE_WIDTH * AMOUNT_OF_TILES_TO_SCROLL;
        }
    }

    function isNavButtonVisible(scrollbarRef: RefObject<HTMLDivElement>, scrollDirection: ScrollDirectionType) {
        const SCROLLBAR_WIDTH =
            scrollbarRef.current && scrollbarRef.current.scrollWidth - scrollbarRef.current.offsetWidth;
        const SCROLLBAR_REACHED_END_POSITION =
            scrollbarRef.current && scrollbarRef.current.scrollLeft === SCROLLBAR_WIDTH;
        const SCROLLBAR_REACHED_START_POSITION = scrollbarRef.current && scrollbarRef.current.scrollLeft === 0;

        if (SCROLLBAR_REACHED_END_POSITION && scrollDirection === SCROLL_DIRECTION_RIGHT) {
            return false;
        } else return !(SCROLLBAR_REACHED_START_POSITION && scrollDirection === SCROLL_DIRECTION_LEFT);
    }

    return (
        <StSliderOuterWrapper>
            <SliderNavButton
                scrollDirection={SCROLL_DIRECTION_LEFT}
                onClick={() => scroll(SCROLL_DIRECTION_RIGHT)}
                isVisible={isLeftNavVisible}
                dataDisplayName={'SliderNavButtonLeft'}
            />
            <StContainerShadowLeft shadowOffsetX={shadowOffsetX} isVisible={isLeftNavVisible} />
            <StSliderWrapper
                ref={scrollbarRef}
                onScroll={() => {
                    handleSetNavButtonVisibility(scrollbarRef);
                }}
                data-displayname={'ProductGroupSlider'}
            >
                <StSliderContainer
                    isAnyTileHovered={isAnyTileHovered}
                    sliderHeight={sliderHeight}
                    sliderHeightHover={sliderHeightHover}
                >
                    {productGroupProducts.length > 0 ? (
                        productGroupProducts.map(productGroupProduct => {
                            return (
                                <StSliderTileComponentWrapper
                                    key={productGroupProduct.id}
                                    onMouseEnter={() => {
                                        setIsAnyTileHovered(true);
                                    }}
                                    onMouseLeave={() => {
                                        setIsAnyTileHovered(false);
                                    }}
                                    data-displayname="SliderTile"
                                >
                                    {cloneElement(SliderTileComponent, {
                                        product: productGroupProduct
                                    })}
                                </StSliderTileComponentWrapper>
                            );
                        })
                    ) : (
                        <NoElementsFound type={NO_ELEMENTS_LC_COLLECTION_TITLES} />
                    )}
                </StSliderContainer>
            </StSliderWrapper>
            <StContainerShadowRight shadowOffsetX={shadowOffsetX} isVisible={isRightNavVisible} />
            <SliderNavButton
                scrollDirection={SCROLL_DIRECTION_RIGHT}
                onClick={() => scroll(SCROLL_DIRECTION_LEFT)}
                isVisible={isRightNavVisible}
                dataDisplayName="SliderNavButtonRight"
            />
        </StSliderOuterWrapper>
    );
}
